// src/forms/shared_form_ux_functions.js
import { form_input_types_string, default_selectors, at_least_one_required_form_identifier_fields } from '../helpers/static_vars';
import { append_organization_id_query_param_to_url } from '../helpers/action_request_helpers';
import { build_data_hub_action_privacy_headers } from '../helpers/data_hub_privacy_policy';
import { fetch_with_timeout, parse_boolean_falsy_types, is_valid_url } from '../helpers/index';
import { prefill_refill_tel_inputs, reset_tel_input_interaction_flags } from '../tel/telephone_sms_inputs'
import { send_clear_jwt_to_cors_iframe } from './cors_iframe';

/**
 * 
 * @param {HTMLElement} form_container 
 * @param {string} submit_button_selector 
 * @returns 
 */
function add_submit_button_loading_state(
    form_container = document, submit_button_selector = '.data-hub-submit-form'
) {
    if (!form_container) {
        return;
    }

    const submit_button = form_container.querySelectorAll(submit_button_selector);
    if (!submit_button || !submit_button.length) {
        return;
    }

	// Loop through all submit buttons and apply loading/disabled state
    submit_button.forEach(button => {
        if (button) {
            const button_text = button.querySelector('.button-text');
            const loader = button.querySelector('.loader-button, .loader');
			
			// Disable the button to prevent double submits
			button.disabled = true;
			button.classList.add('opacity-50', 'cursor-not-allowed');

			// If the template includes a text node and loader, toggle them
			if (button_text && loader) {
				button_text.classList.add('hidden');
				loader.classList.remove('hidden');
			}
        }
    });
}

/**
 * 
 * @param {HTMLElement} form_container 
 * @param {string} submit_button_selector 
 */
function remove_submit_button_loading_state(
    form_container = document, submit_button_selector = default_selectors.submit_button_selector
) {

    if (!form_container) {
        return;
    }

    const submit_button = form_container.querySelectorAll(submit_button_selector);
    if (!submit_button || !submit_button.length) {
        return;
    }

	// Loop through all submit buttons and remove loading/disabled state
    submit_button.forEach(button => {
        if (button) {
            const button_text = button.querySelector('.button-text');
            const loader = button.querySelector('.loader-button, .loader');

			// Re-enable the button and remove loading styles
			button.disabled = false;
			button.classList.remove('opacity-50', 'cursor-not-allowed');

			// If the template includes a text node and loader, toggle them back
			if (button_text && loader) {
				button_text.classList.remove('hidden');
				loader.classList.add('hidden');
			}
        }
    });
}

/**
 * Adds a loading overlay to the form container.
 * @param {HTMLElement|Document|ShadowRoot} form_container - The container element to add the loading overlay to
 * @param {string} loading_overlay_selector - CSS selector for existing overlay elements (default: default_selectors.loading_overlay_selector)
 * @param {Object} data_hub_instance - Data hub instance for error reporting
 * @returns {void}
 */
function add_form_loading_overlay(
    form_container = document,
    loading_overlay_selector = default_selectors.loading_overlay_selector,
    data_hub_instance = window?.data_hub
) {
    if (!form_container) { return data_hub_instance?.error_function?.('Missing form_container in add_form_loading_overlay'); }
    if (!loading_overlay_selector) { loading_overlay_selector = default_selectors.loading_overlay_selector; }

    // Must be an html element, document, or shadow root
    if (form_container instanceof HTMLElement || form_container instanceof Document || form_container instanceof ShadowRoot) {
        const form_overlay_class_elements = form_container.querySelectorAll(loading_overlay_selector);
        if (form_overlay_class_elements.length) {
            form_overlay_class_elements.forEach(overlay => {
                overlay.classList.remove('hidden');
            });
        } else {
            const form_overlay = document.createElement('div');
            form_overlay.classList.add('data-hub-loading-overlay', 'fixed', 'inset-0', 'bg-white',
                'bg-opacity-50', 'flex', 'justify-center', 'items-center', 'z-50');
            form_overlay.innerHTML = '<div class="loader"></div>';

            // Determine the appropriate container to append to
            let targetContainer;
            if (form_container === document) {
                targetContainer = document.body;
            } else {
                targetContainer = form_container;
            }

            try {
                targetContainer.appendChild(form_overlay);
            } catch (error) {
                // If we hit a hierarchy error, try document.body as a fallback
                if (error.name === 'HierarchyRequestError') {
                    try {
                        document.body.appendChild(form_overlay);
                    } catch (secondError) {
                        data_hub_instance?.error_function?.(
                            'Failed to add overlay to document.body:' + secondError
                        );
                    }
                } else {
                    data_hub_instance?.error_function?.(
                        'Failed to add overlay to target container:' + error
                    );
                }
            }
        }
    } else {
        data_hub_instance?.error_function?.('Invalid form_container provided:', form_container);
    }
}

/**
 * Removes the loading overlay from the form container.
 * @param {HTMLElement|Document|ShadowRoot} form_container - The container element from which to remove the loading overlay
 * @param {string} loading_overlay_selector - CSS selector for overlay elements to hide (default: default_selectors.loading_overlay_selector)
 * @param {Object} data_hub_instance - Data hub instance for error reporting
 * @returns {void}
 */
function remove_form_loading_overlay(
    form_container = document,
    loading_overlay_selector = default_selectors.loading_overlay_selector,
    data_hub_instance = window?.data_hub
) {
    if (!loading_overlay_selector) { loading_overlay_selector = default_selectors.loading_overlay_selector; }
    if (!form_container) { return data_hub_instance?.error_function?.('Missing form_container in remove_form_loading_overlay'); }
    // any overlay divs that are hidden
    if (form_container instanceof HTMLElement || form_container instanceof Document || form_container instanceof ShadowRoot) {
        const form_overlay_class_elements = form_container.querySelectorAll(loading_overlay_selector);
        if (form_overlay_class_elements.length) {
            form_overlay_class_elements.forEach(overlay => {
                overlay.classList.add('hidden');
            });
        } else {
            // No overlay elements found — expected when overlay wasn't created
        }
    } else {
        data_hub_instance?.error_function?.('Invalid form_container provided:' + form_container);
    }
}

/**
 * Adds a loading state to form inputs elements
 * @param {HTMLElement} form_container the dom element to find the inputs
 * @param {string} prefill_input_selector the selector for the input elements to apply loading state
 * @param {Object} data_hub_instance the instance of the data hub
 * @returns 
 */
function add_form_input_loading_state(
    form_container = document,
    prefill_input_selector = form_input_types_string,
    data_hub_instance = window?.data_hub
) {
    if (!form_container || !prefill_input_selector) { return; }

    if (form_container instanceof HTMLElement || form_container instanceof Document || form_container instanceof ShadowRoot) {
        form_container.querySelectorAll(prefill_input_selector).forEach(field => {
            field.classList.add('input-loading');

            // Track user modifications per-field so prefill can skip fields the user has already typed into.
            // Uses { once: true } so the listener auto-removes after first trigger.
            const markModified = function () {
                this.setAttribute('data-user-modified', 'true');
            };
            field.addEventListener('input', markModified, { once: true });
            if (field.tagName === 'SELECT') {
                field.addEventListener('change', markModified, { once: true });
                field.disabled = true;
            } else if (field.tagName === 'INPUT' || field.tagName === 'TEXTAREA') {
                field.readOnly = true;
                field.classList.add('input-loading-bkg-image');
            }
        });

    } else {
        data_hub_instance?.error_function?.('Invalid form_container provided:' + form_container);
    }
}

/**
 * Removes the loading state from form inputs elements
 * @param {HTMLElement} form_container the dom element to find the inputs
 * @param {string} prefill_input_selector the selector for the input elements to remove loading state
 * @param {Object} data_hub_instance the instance of the data hub
 * @returns
 */
function remove_form_input_loading_state(
    form_container = document,
    prefill_input_selector = form_input_types_string,
    data_hub_instance = window?.data_hub
) {
    //form_input_types_string
    if (!form_container || !prefill_input_selector) { return; }
    if (form_container instanceof HTMLElement || form_container instanceof Document || form_container instanceof ShadowRoot) {
        form_container.querySelectorAll(prefill_input_selector).forEach(field => {
            field.classList.remove('input-loading');
            // look at what type of field it is, and make them clickable / changeable
            if (field.tagName === 'SELECT') {
                field.disabled = false;
            } else if (field.tagName === 'INPUT' || field.tagName === 'TEXTAREA') {
                field.readOnly = false;
                field.classList.remove('input-loading-bkg-image');
            }
        });
    } else {
        data_hub_instance?.error_function?.('Invalid form_container provided:' + form_container);
    }
}

/**
 * Clears the JWT cookie and partitioned cookies via the clear endpoint.
 * The backend resets the cookie to a fresh unknown_uuid JWT and returns it.
 * Uses iframe passthrough for cross-origin, direct fetch for same-origin.
 * Fire-and-forget — does not block the caller.
 *
 * When the response arrives, delegates to data_hub_instance.got_clear_jwt_response()
 * which handles setting jwt_cookie, user_data, re-prefilling forms, and dispatching events.
 *
 * Backend expected response: { returns: { jwt_cookie: "eyJ...", user_data: { global_uuid, global_uuid_source, uuids } } }
 *
 * @param {string} forms_domain - The domain to clear the cookies from.
 * @param {function} error_function - The function to call in case of an error.
 * @param {Object} data_hub_instance - The instance of the data hub.
 */
function clear_jwt_cookie(
    forms_domain = window?.data_hub?.forms_domain,
    error_function = window?.data_hub?.error_function,
    data_hub_instance = window?.data_hub
) {
    try {
        if (!forms_domain) {
            return error_function?.('Missing forms_domain at clear_jwt_cookie');
        }

        // Cross-origin: use iframe passthrough so the request runs in the forms domain context
        // The iframe will fetch same-origin, parse the response, and postMessage back
        // jwt_cookie + user_data via DATA_HUB_CLEAR_JWT_COOKIE_SUCCESS → got_clear_jwt_response()
        if (forms_domain !== window.location.origin) {
            send_clear_jwt_to_cors_iframe(forms_domain, data_hub_instance);
            return;
        }

        // Same-origin: direct fetch, fire-and-forget
        let requestDomain = `${forms_domain}/api/get/clear_jwt_and_partitioned_cookies`;
        requestDomain = append_organization_id_query_param_to_url(requestDomain, data_hub_instance?.organization_id);
        fetch_with_timeout(requestDomain, {
            method: 'GET',
            credentials: 'include',
            headers: {
                ...build_data_hub_action_privacy_headers(data_hub_instance),
            },
            timeout: 20000
        })
            .then(response => {
                if (!response?.ok) {
                    error_function?.('Error at clear_jwt_cookie: Network response was not ok');
                    return;
                }
                return response.json();
            })
            .then(data => {
                if (!data?.returns) return;
                const dh = data_hub_instance || window?.data_hub;
                if (dh && typeof dh.got_clear_jwt_response === 'function') {
                    dh.got_clear_jwt_response(data.returns);
                }
            })
            .catch((error) => {
                error_function?.('Error at clear_jwt_cookie: ' + error);
            });
    } catch (e) {
        error_function?.('Error at clear_jwt_cookie: ' + e);
    }
}

/**
 * Resets the form fields to their default state.
 * @param {Event} event - The event object.
 * @param {HTMLElement} form_container - The form container element.
 * @param {string} prefill_input_selector - The selector for the input elements to reset.
 * @param {string} reset_button_selector - The selector for the reset button.
 * @param {Object} data_hub_instance - The instance of the data hub.
 * @param {boolean} reset_data_hub_user_data - Whether to reset the data hub user data.
 * @returns {void}
 */
/**
 * Clears all prefillable input elements within a form container.
 * Sets text fields to '', unchecks checkboxes/radios, resets selects to first option,
 * clears intl-tel-input instances, removes data-user-modified, and resets tel interaction flags.
 * Does NOT touch user_data or window.data_hub_user_data — that's the caller's responsibility.
 *
 * @param {HTMLElement} form_container - The form container to clear inputs within.
 * @param {string} prefill_input_selector - The selector for prefillable input elements.
 */
function clear_form_inputs(
    form_container,
    prefill_input_selector = default_selectors.prefill_input_selector
) {
    const elements = form_container.querySelectorAll(prefill_input_selector);

    Array.from(elements).forEach(element => {
        if (['checkbox', 'radio'].includes(element.type)) {
            element.checked = false;
        } else if (element.type === 'tel') {
            // Clear intl-tel-input instance if present, otherwise clear raw value
            if (element.itiInstance) {
                element.itiInstance.setNumber('');
            } else {
                element.value = '';
            }
        } else if (['text', 'textarea', 'email', 'password', 'url', 'hidden', 'number', 'date', 'datetime-local', 'month', 'time', 'week'].includes(element.type)) {
            element.value = '';
        } else if (element.tagName === 'SELECT') {
            element.value = element.options.length > 0 ? element.options[0].value : '';
        }

        element.removeAttribute('data-user-modified');
    });

    reset_tel_input_interaction_flags(form_container);
}

function reset_form(
    event,
    form_container,
    prefill_input_selector = default_selectors.prefill_input_selector,
    reset_button_selector = default_selectors.reset_button_selector,
    data_hub_instance = window?.data_hub,
    reset_data_hub_user_data = true
) {
    event.preventDefault();

    clear_form_inputs(form_container, prefill_input_selector);

    const reset_button = form_container.querySelector(reset_button_selector);
    if (reset_button) {
        reset_button.classList.add('hidden');
    }

    if (reset_data_hub_user_data && typeof data_hub_instance !== 'undefined') {
        data_hub_instance.user_data = {};
        data_hub_instance.jwt_cookie = null;
    }
    window.data_hub_user_data = {};
}

/**
 * Checks whether user_data represents a known visitor identity worth offering a reset for.
 * Unknown UUID-only shells are browser tracking state, not human-facing prefill.
 * @param {Object} user_data - Candidate user data merged from URL params, localStorage, or JWT.
 * @returns {boolean}
 */
function has_meaningful_user_data_for_reset_button(user_data = {}) {
    if (!user_data || typeof user_data !== 'object' || Array.isArray(user_data)) {
        return false;
    }

    // Only a real contactable identifier (email / phone, per the shared static var list)
    // counts as a known visitor worth offering a reset for. A brand-new session that only
    // carries tracking state — global_uuid + global_uuid_source: 'unknown_uuid' + uuids[] —
    // has no primary identifier here, so this returns false and the reset/"Not you?" UI
    // stays hidden for anonymous visitors.
    return at_least_one_required_form_identifier_fields.some((identifier_field_name) => {
        const identifier_field_value = user_data[identifier_field_name];
        return identifier_field_value !== null &&
            identifier_field_value !== undefined &&
            String(identifier_field_value).trim() !== '';
    });
}

/**
 * Checks if the form has user data and shows the reset button if it does.
 * @param {HTMLElement} form_container - The form container element.
 * @param {string} reset_button_selector - The selector for the reset button.
 * @param {string} prefill_input_selector - The selector for the input elements to reset.
 * @param {Object} data_hub_instance - The instance of the data hub.
 * @param {boolean} reset_data_hub_user_data - Whether to reset the data hub user data.
 * @returns {void}
 */
function if_has_user_data_show_reset_button(
    form_container,
    reset_button_selector = default_selectors.reset_button_selector,
    prefill_input_selector = default_selectors.prefill_input_selector,
    data_hub_instance = window?.data_hub || {},
    reset_data_hub_user_data = true
) {
    const reset_button = form_container.querySelector(reset_button_selector);
    if (reset_button) {
        reset_button.onclick = (event) => {
            event.preventDefault();
            reset_form(
                event,
                form_container,
                prefill_input_selector,
                reset_button_selector,
                data_hub_instance,
                reset_data_hub_user_data
            );
            clear_jwt_cookie(data_hub_instance?.forms_domain, data_hub_instance?.error_function, data_hub_instance);
        };

        const user_data_for_reset_check = data_hub_instance?.user_data || window?.data_hub_user_data || {};
        reset_button.classList.toggle(
            'hidden',
            !has_meaningful_user_data_for_reset_button(user_data_for_reset_check)
        );
    }

}

/**
 * Helper to fill in form element with data based on element type
 * @param {HTMLElement} element - The form element to fill.
 * @param {string} key - The key to use for the value.
 * @param {string} value - The value to fill in.
 * @param {Object} data_hub_instance - The instance of the data hub.
 * @returns {void}
 */
function fill_element_with_data(element, key, value, data_hub_instance) {
    // Skip fields the user has already typed into — don't overwrite their input
    if (element.getAttribute('data-user-modified') === 'true') {
        return;
    }

    if (element.tagName === 'INPUT') {
        if (['checkbox', 'radio'].includes(element.type)) {
            element.checked = parse_boolean_falsy_types(value);
        } else if (['text', 'email', 'tel', 'password', 'hidden', 'date', 'datetime-local', 'month', 'time', 'week'].includes(element.type)) {
            element.value = value;
        } else if (element.type === 'url') {
            is_valid_url(value) ? element.value = value : data_hub_instance?.error_function?.('prefill_form invalid url: ' + key + ' ' + value, null);
        } else if (element.type === 'number') {
            element.value = parseFloat(value);
        } else {
            data_hub_instance?.error_function?.('prefill_form non-form element: ' + element.tagName + ' ' + key + ' ' + value, null);

        }
    } else if (element.tagName === 'SELECT' || element.tagName === 'TEXTAREA') {
        element.value = value;
    } else {
        data_hub_instance?.error_function?.('prefill_form non-form element: ' + element.tagName + ' ' + key + ' ' + value, null);
    }
}

/**
 * Re pre-fills all form input, checkbox etc elements
 * @param {Object} user_data - The user data object used to prefill data.
 * @param {string} prefill_input_selector - The selector for the input elements to prefill.
 * @param {Object} data_hub_instance - The instance of the data hub.
 */
function re_prefill_all_forms(
    user_data = window?.data_hub?.user_data || {},
    prefill_input_selector = default_selectors.prefill_input_selector,
    data_hub_instance = window?.data_hub
) {
    // Loop through forms prefilled by this instance
    if (data_hub_instance?.prefilled_forms) {
        data_hub_instance.prefilled_forms.forEach((form_container) => {
            prefill_form(form_container, user_data, prefill_input_selector, default_selectors.reset_button_selector, undefined, true, data_hub_instance);
        });
    }
}

/**
 * Prefills the form with user data.
 * @param {HTMLElement} form_container - The form container element.
 * @param {Object} user_data - The user data object used to prefill data.
 * @param {string} prefill_input_selector - The selector for the input elements to prefill.
 * @param {string} reset_button_selector - The selector for the reset button.
 * @param {string} form_id - The ID of the form.
 * @param {boolean} allow_re_prefill - Whether to allow re-prefilling of the form.
 * @param {Object} data_hub_instance - The instance of the data hub.
 * @returns {void}
 */
function prefill_form(
    form_container,
    user_data = window?.data_hub?.user_data || {},
    prefill_input_selector = default_selectors.prefill_input_selector,
    reset_button_selector = default_selectors.reset_button_selector,
    form_id = null,
    allow_re_prefill = false,
    data_hub_instance = window?.data_hub // Instance parameter
) {
    // Check if the form has already been prefilled
    if (!allow_re_prefill && data_hub_instance?.prefilled_forms?.has(form_container)) {
        return;
    }

    // Select all prefillable elements in the form container
    const elements = form_container.querySelectorAll(prefill_input_selector);
    if (!elements.length) {
        return;
    }

    let found_at_least_one_prefill = false;

    // Prefill each element if data is available
    elements.forEach(element => {
        const key = element.id || element.name;

        if (user_data && user_data.hasOwnProperty(key)) {
            const value = user_data[key];
            if (value !== null && value !== '') {
                found_at_least_one_prefill = true;
                // Skip element type tel will bind with tel intl input functions
                if (element.type === 'tel') {
                    return;
                }
                fill_element_with_data(element, key, value, data_hub_instance);
            }
        }
    });

    // Mark the form as prefilled in the Set
    // defined in datahub as this.data_hub_prefilled_forms = new Set();
    if (data_hub_instance) {
        // check if  data_hub_instance.prefilled_forms exists and is a set. if not create one
        if (!data_hub_instance.prefilled_forms) {
            data_hub_instance.prefilled_forms = new Set();
        }
        data_hub_instance.prefilled_forms.add(form_container);
    }

    // Show reset button only for real known user data, not unknown_uuid-only JWT shells.
    if (found_at_least_one_prefill && has_meaningful_user_data_for_reset_button(user_data)) {
        if_has_user_data_show_reset_button(
            form_container,
            reset_button_selector,
            prefill_input_selector,
            data_hub_instance,
            true
        );
    }

    prefill_refill_tel_inputs(user_data, form_container, data_hub_instance?.error_function, data_hub_instance);

    // Remove loading states from form fields
    remove_form_input_loading_state(form_container, prefill_input_selector);
    remove_form_loading_overlay(form_container);

}

/**
 * Resets the UI from success state back to form state.
 * @param {HTMLElement} form_container - The form container element.
 */
function reset_ui_from_success_to_form(form_container, data_hub_instance = window?.data_hub) {
    // Hide all success elements
    const success_elements = form_container.querySelectorAll(default_selectors.show_on_return_selector);
    success_elements.forEach(el => {
        el.classList.add('hidden');
    });

    // Ensure loading overlay is hidden (managed separately, not by hide-on-return-function)
    remove_form_loading_overlay(form_container, default_selectors.loading_overlay_selector, data_hub_instance);

    // Remove hidden if applied to hide_on_return_selector (show the form again)
    // Exclude loading overlays - they are managed by add/remove_form_loading_overlay functions
    const hide_on_return_elements = form_container.querySelectorAll(
        `${default_selectors.hide_on_return_selector}:not(.${default_selectors.loading_overlay_selector.replace('.', '')})`
    );
    hide_on_return_elements.forEach(el => {
        // Double-check: don't show loading overlays (they should stay hidden)
        if (!el.classList.contains('data-hub-loading-overlay')) {
            el.classList.remove('hidden');
        }
    });
}

/**
 * Clears form loading states and returns an error.
 * @param {HTMLElement} form_container - The form container element.
 * @param {string} [internal_error_message] - Internal error message.
 * @param {string} [external_error_message] - Message to display to the user.
 * @param {Object} [data_hub_instance] - The instance of the data hub.
 * @returns {void}
 */
function return_error_clear_form_loading_state(
    form_container,
    internal_error_message = 'Error submitting form data',
    external_error_message = 'Error submitting your request. Refresh page to try again or contact support.',
    data_hub_instance = window?.data_hub,
) {
    remove_submit_button_loading_state(form_container, data_hub_instance?.submit_button_selector);
    remove_form_loading_overlay(form_container);
    remove_form_input_loading_state(form_container);

    // Reset UI from success state to form state (will also ensure loading overlay stays hidden)
    reset_ui_from_success_to_form(form_container, data_hub_instance);

    return data_hub_instance?.error_function?.(internal_error_message, external_error_message);
}

/**
 * Hides specified elements on form submission.
 * @param {string|HTMLElement|NodeList|Array} [selector=data_hub.hide_on_submit_selector] - Selector or element(s) to hide.
 * @param {HTMLElement|null} [form_container=null] - The form container element.
 */
function hide_on_submit(selector, form_container = null) {
    let hide_on_submit_elements = null;

    if (typeof selector === 'string') {
        if (form_container instanceof HTMLElement) {
            hide_on_submit_elements = form_container.querySelectorAll(selector);
        } else {
            hide_on_submit_elements = document.querySelectorAll(selector);
        }
    } else if (selector instanceof HTMLElement) {
        hide_on_submit_elements = [selector];
    } else if (selector instanceof NodeList || Array.isArray(selector)) {
        hide_on_submit_elements = selector;
    }

    if (hide_on_submit_elements && hide_on_submit_elements.length > 0) {
        hide_on_submit_elements.forEach(element => {
            element.classList.add('hidden');
        });
    }
}

/**
 * Clears all prefillable form inputs across all prefilled forms.
 * Unlike re_prefill_all_forms (which only sets values for matching keys),
 * this actively blanks every input using clear_form_inputs.
 * Also hides reset buttons since there's nothing to reset.
 * Does NOT clear user_data — that's the caller's responsibility.
 *
 * @param {Object} data_hub_instance - The data_hub class instance.
 */
function clear_all_form_inputs(data_hub_instance = window?.data_hub) {
    const prefill_input_selector = data_hub_instance?.prefill_input_selector || default_selectors.prefill_input_selector;
    const reset_button_selector = data_hub_instance?.reset_button_selector || default_selectors.reset_button_selector;

    if (!data_hub_instance?.prefilled_forms) return;

    data_hub_instance.prefilled_forms.forEach((form_container) => {
        clear_form_inputs(form_container, prefill_input_selector);

        const reset_button = form_container.querySelector(reset_button_selector);
        if (reset_button) {
            reset_button.classList.add('hidden');
        }
    });
}

export {
    add_form_input_loading_state,
    remove_form_input_loading_state,
    add_form_loading_overlay,
    remove_form_loading_overlay,
    add_submit_button_loading_state,
    remove_submit_button_loading_state,
    prefill_form,
    re_prefill_all_forms,
    clear_all_form_inputs,
    return_error_clear_form_loading_state,
    hide_on_submit,
    reset_ui_from_success_to_form,
    clear_jwt_cookie
}
